import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { ExportToCsv } from 'export-to-csv';

import { Moves } from '@gql/base-types';

import { formatTime, formatPhone } from '@services/formatService';
import { getFormattedVehicleFromMove, getFormattedMoveTypeFromMove } from '@services/moveService';
import { getFormattedCombinedStatusFromMove } from '@services/statusService';
import { getWorkflowData } from '@services/utilityService';

// Helper function to check if pickup time is within the last day
const isPickupWithinLastDay = (pickupTime: string) => {
  if (!pickupTime) return false;
  const oneDayAgo = dayjs().subtract(1, 'day');
  const pickup = dayjs(pickupTime);
  return pickup.isAfter(oneDayAgo);
};

export const generateMovesCSV = (moves: Moves[], start: string, end: string, isAdmin: boolean = false) => {
  if (moves && moves.length > 0) {
    try {
      const gatherExtraPickupHeaders = (moves: Moves[]) => {
        let extraPickupHeaders: string[] = [];
        moves.forEach(move => {
          const pickupWorkflowData = getWorkflowData('pickup', move.pickup_workflow_data, 'csv');
          if (pickupWorkflowData && Object.keys(pickupWorkflowData).length > 0) {
            const workflowKeys = Object.keys(pickupWorkflowData);
            extraPickupHeaders = [...extraPickupHeaders, ...workflowKeys];
          }
        });
        return [...new Set(extraPickupHeaders)];
      };
      const extraPickupHeaders = gatherExtraPickupHeaders(moves);

      const gatherExtraDeliveryHeaders = (moves: Moves[]) => {
        let extraDeliveryHeaders: string[] = [];
        moves.forEach(move => {
          const deliveryWorkflowData = getWorkflowData('delivery', move.delivery_workflow_data, 'csv');
          if (deliveryWorkflowData && Object.keys(deliveryWorkflowData).length > 0) {
            const workflowKeys = Object.keys(deliveryWorkflowData);
            extraDeliveryHeaders = [...extraDeliveryHeaders, ...workflowKeys];
          }
        });
        return [...new Set(extraDeliveryHeaders)];
      };
      const extraDeliveryHeaders = gatherExtraDeliveryHeaders(moves);

      const createCsvRow = (move: Moves) => {
        const pickupWorkflowData = (getWorkflowData('pickup', move.pickup_workflow_data, 'csv') || {}) as Record<
          string,
          unknown
        >;
        const extraPickupData = {} as Record<string, unknown>;
        extraPickupHeaders.forEach(key => {
          extraPickupData[key] = pickupWorkflowData[key] !== undefined ? pickupWorkflowData[key] : '';
        });

        const deliveryWorkflowData = (getWorkflowData('delivery', move.delivery_workflow_data, 'csv') || {}) as Record<
          string,
          unknown
        >;
        const extraDeliveryData = {} as Record<string, unknown>;
        extraDeliveryHeaders.forEach(key => {
          extraDeliveryData[key] = deliveryWorkflowData[key] !== undefined ? deliveryWorkflowData[key] : '';
        });

        return {
          move_id: move.id,
          status: getFormattedCombinedStatusFromMove(move) || '',
          ...(isAdmin ? { market: move?.customer?.name || '' } : {}),
          payer: move?.payer_id || '',
          type: getFormattedMoveTypeFromMove(move) || '',
          consumer_name: move?.consumer_name || '',
          consumer_phone: formatPhone(move?.consumer_phone || '') || '',
          ready_by: formatTime(move?.ready_by || '') || '',
          time_completed: formatTime(move?.delivery_successful || '') || '',
          time_canceled: formatTime(move?.canceled_at || '') || '',
          cancel_reason: move?.cancel_reason || '',
          reference_num: move.reference_num || '',
          vehicle_stock: move.vehicle_stock || '',
          vehicle_vin: move.vehicle_vin || '',
          vehicle_make: move.vehicle_make || '',
          vehicle_model: move.vehicle_model || '',
          vehicle_year: move.vehicle_year || '',
          vehicle_color: move.vehicle_color || '',
          vehicle_odometer: move.vehicle_odometer || '',
          formatted_vehicle: getFormattedVehicleFromMove(move) || '',
          lane: move?.lane?.description || '',
          distance: move?.lane?.distance_miles || 0,
          created_by: move?.createdBy && move?.createdBy?.includes('@') ? move.createdBy : '',
          ...(isAdmin ? { workflow: move?.workflowset?.name || '' } : {}),
          ...extraPickupData,
          ...extraDeliveryData,
        };
      };
      const csvRows = moves.map(move => createCsvRow(move));

      const csvOptions = {
        filename: `moves_dashboard_${dayjs(start).format('MM-DD-YYYY')}_to_${dayjs(end).format('MM-DD-YYYY')}`,
        useKeysAsHeaders: true,
      };

      const csvExporter = new ExportToCsv(csvOptions);
      csvExporter.generateCsv(csvRows);
      toast.success('Generated CSV!', { autoClose: 2000, toastId: 'csv-generated' });
    } catch (err) {
      toast.error('Failed to generate CSV!');
      console.error('Failed to generate CSV:', err);
    }
  } else {
    toast.error('Failed to generate CSV - No moves found!');
    console.error('Failed to generate CSV - No moves found!');
  }
};

export const generateRidehailsCSV = (ridehails: any[], start: string, end: string) => {
  if (ridehails && ridehails.length > 0) {
    try {
      const createCsvRow = (ridehail: any) => {
        const move = ridehail?.move;
        const parentMove = move?.parentMove;
        const driver = move?.driver;
        const lane = move?.lane;
        const pickupTime = ridehail?.pickup_time || move?.ready_by;
        const hasStatus = ridehail?.status || move?.status;

        let displayStatus;
        if (move?.cancel_status) {
          displayStatus = `Canceled: ${move?.cancel_reason || 'Unknown'}`;
        } else if ((!hasStatus || hasStatus.toLowerCase() === 'not called') && pickupTime && isPickupWithinLastDay(pickupTime)) {
          displayStatus = 'Pending';
        } else {
          // Format status
          const status = hasStatus || '';
          if (status.toLowerCase() === 'droppedoff') {
            displayStatus = 'Completed';
          } else {
            displayStatus = status.replace(/_/g, ' ').split(' ').map((word: string) =>
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            ).join(' ');
          }
        }

        const laneText = lane?.pickup?.name && lane?.delivery?.name
          ? `${lane.pickup.name} to ${lane.delivery.name}`
          : lane?.description || ridehail?.pickup_address || '';

        const distance = ridehail?.distance_miles || lane?.distance_miles || '';
        const actualPrice = Math.ceil(ridehail?.actual_price || 0);
        const priceRange = actualPrice && actualPrice > 0 ? `$${actualPrice} - $${actualPrice + 5}` : '';

        return {
          drive_id: move?.parent_move_id || '',
          consumer: parentMove?.consumer_name || '',
          reference_num: parentMove?.reference_num || '',
          vin: parentMove?.vehicle_vin || '',
          provider: ridehail?.provider || '',
          status: displayStatus,
          driver: driver?.user?.display_name || '',
          planned_time: pickupTime ? formatTime(pickupTime) : '',
          lane: laneText,
          distance_miles: distance,
          price: priceRange,
        };
      };

      const csvRows = ridehails.map(ridehail => createCsvRow(ridehail));

      const csvOptions = {
        filename: `rideshares_${dayjs(start).format('MM-DD-YYYY')}_to_${dayjs(end).format('MM-DD-YYYY')}`,
        useKeysAsHeaders: true,
      };

      const csvExporter = new ExportToCsv(csvOptions);
      csvExporter.generateCsv(csvRows);
      toast.success('Generated CSV!', { autoClose: 2000, toastId: 'csv-generated' });
    } catch (err) {
      toast.error('Failed to generate CSV!');
      console.error('Failed to generate CSV:', err);
    }
  } else {
    toast.error('Failed to generate CSV - No rideshares found!');
    console.error('Failed to generate CSV - No rideshares found!');
  }
};
